// src/components/ReportView.js

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { API_URL } from './apiurl';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

// Custom Date Input Component
const CustomDateInput = React.forwardRef(({ value, onClick, onChange, placeholder }, ref) => (
  <input
    type="text"
    onClick={onClick}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    ref={ref}
    style={{
      padding: '0.6rem',
      fontSize: '1rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      cursor: 'pointer',
      width: '100%',
      boxSizing: 'border-box',
    }}
    readOnly
  />
));

// CSV-Export-Helfer
function exportToCSV(data, filename = 'report.csv') {
  if (!data || !data.length) {
    toast.warning('Keine Daten vorhanden');
    return;
  }
  const headers = Object.keys(data[0]); // Spaltenüberschriften
  const csvRows = [];
  // Header
  csvRows.push(headers.join(';'));
  // Datenzeilen
  data.forEach(obj => {
    const row = headers.map(h => {
      const val = obj[h];
      if (typeof val === 'string') {
        return `"${val.replace(/"/g, '').replace(/;/g, ',')}"`;
      }
      return val;
    }).join(';');
    csvRows.push(row);
  });

  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function ReportView() {
  const { auth } = useContext(AuthContext);
  const [allEntries, setAllEntries] = useState([]); // Alle Einträge
  const [filteredEntries, setFilteredEntries] = useState([]); // Gefilterte
  const [customers, setCustomers] = useState([]); // Für Kunde-Filter
  const [types, setTypes] = useState([]);         // Für Arbeitstyp-Filter

  // Filter States
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Lade-/Fehlerzustand
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 1. Laden der Daten
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      // a) Einträge
      const respEntries = await fetch(`${API_URL}/entries`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!respEntries.ok) {
        throw new Error('Fehler beim Laden der Einträge');
      }
      const dataEntries = await respEntries.json();

      // b) Kunden
      const respCustomers = await fetch(`${API_URL}/customers`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!respCustomers.ok) {
        throw new Error('Fehler beim Laden der Kunden');
      }
      const dataCustomers = await respCustomers.json();

      // c) Typen (Arbeitstypen)
      const respTypes = await fetch(`${API_URL}/types`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!respTypes.ok) {
        throw new Error('Fehler beim Laden der Arbeitstypen');
      }
      const dataTypes = await respTypes.json();

      setAllEntries(dataEntries);
      setFilteredEntries(dataEntries); // Anfangs ungefiltert
      setCustomers(dataCustomers);
      setTypes(dataTypes);
    } catch (err) {
      console.error('Fehler in fetchAllData:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 2. Filtern
  useEffect(() => {
    applyFilter();
  }, [selectedCustomer, selectedType, startDate, endDate, allEntries]);

  const applyFilter = () => {
    if (!allEntries.length) {
      setFilteredEntries([]);
      return;
    }
    let result = [...allEntries];

    // Filter: Kunde
    if (selectedCustomer) {
      result = result.filter(e => e.customerId === parseInt(selectedCustomer, 10));
    }
    // Filter: Type
    if (selectedType) {
      result = result.filter(e => e.type === selectedType);
    }
    // Filter: Start/Enddatum
    if (startDate) {
      const startStr = startDate.toISOString().split('T')[0];
      result = result.filter(e => e.date >= startStr);
    }
    if (endDate) {
      const endStr = endDate.toISOString().split('T')[0];
      result = result.filter(e => e.date <= endStr);
    }

    setFilteredEntries(result);
  };

  // 3. CSV-Export
  const handleExportCSV = () => {
    const dataForCSV = filteredEntries.map(e => ({
      id: e.id,
      date: e.date,
      type: e.type,
      subject: e.subject,
      address: e.address,
      hours: e.hours,
      hotelspeesen: e.hotelspeesen ? 'Ja' : 'Nein',
      verpflegungsMehraufwand: e.verpflegungsMehraufwand ? 'Ja' : 'Nein',
      customerId: e.customerId,
      hourlyWage: e.hourlyWage,
      fachgebiet: e.fachgebiet === 1 ? 'Workflow' : e.fachgebiet === 2 ? 'Finanzbuchhaltung' : 'Nein',
      homeoffice: e.homeoffice ? 'Ja' : 'Nein',
    }));
    exportToCSV(dataForCSV, 'report.csv');
  };

  // 4. Summen berechnen
  const totalHours = filteredEntries.reduce((acc, e) => acc + (e.hours || 0), 0);

  // 5. Kleines Beispiel-Chart
  const buildChartData = () => {
    if (!filteredEntries.length) return [];
    const sums = {};
    filteredEntries.forEach(e => {
      const cId = e.customerId || 0;
      if (!sums[cId]) sums[cId] = 0;
      sums[cId] += e.hours || 0;
    });
    return Object.keys(sums).map(cIdStr => {
      const cid = parseInt(cIdStr, 10);
      const cust = customers.find(c => c.id === cid);
      const label = cust ? (cust.vorname + ' ' + cust.nachname).trim() : `Kunde ${cid}`;
      return { name: label, hours: sums[cIdStr] };
    });
  };
  const chartData = buildChartData();

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Report - Alle Einträge</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>Daten werden geladen...</p>}

      {/* Filter UI */}
      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <label>Kunde:</label>
          <select
            style={styles.select}
            value={selectedCustomer}
            onChange={e => setSelectedCustomer(e.target.value)}
          >
            <option value="">(Alle)</option>
            {customers.map(c => (
              <option key={c.id} value={c.id}>
                {c.vorname} {c.nachname}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label>Arbeitstyp:</label>
          <select
            style={styles.select}
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
          >
            <option value="">(Alle)</option>
            {types.map(t => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label>Startdatum:</label>
          <DatePicker
            selected={startDate}
            onChange={date => setStartDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="Startdatum"
            customInput={<CustomDateInput />}
          />
        </div>

        <div style={styles.filterGroup}>
          <label>Enddatum:</label>
          <DatePicker
            selected={endDate}
            onChange={date => setEndDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="Enddatum"
            customInput={<CustomDateInput />}
          />
        </div>

        {/* CSV Export */}
        <button style={styles.exportBtn} onClick={handleExportCSV}>
          Export CSV
        </button>
      </div>

      {/* Zusammenfassung */}
      <div style={styles.summaryBox}>
        <p>
          Anzahl gefilterte Einträge: <strong>{filteredEntries.length}</strong>
        </p>
        <p>
          Gesamte Stunden: <strong>{totalHours.toFixed(2)}</strong>
        </p>
      </div>

      {/* Tabelle - in scrollbarem Container */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Datum</th>
              <th style={styles.tableHeader}>Typ</th>
              <th style={styles.tableHeader}>Betreff</th>
              <th style={styles.tableHeader}>Stunden</th>
              <th style={styles.tableHeader}>Hotelspeesen</th>
              <th style={styles.tableHeader}>Verpflegungs-<br/>Mehraufwand</th>
              <th style={styles.tableHeader}>Fachgebiet</th>
              <th style={styles.tableHeader}>Homeoffice</th>
              <th style={styles.tableHeader}>Kunde</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map(e => {
              const cust = customers.find(c => c.id === e.customerId);
              const custName = cust
                ? (cust.vorname + ' ' + cust.nachname).trim()
                : e.customerId || '-';
              return (
                <tr key={e.id}>
                  <td style={styles.tableCell}>{e.date}</td>
                  <td style={styles.tableCell}>{e.type}</td>
                  <td style={styles.tableCell}>{e.subject}</td>
                  <td style={styles.tableCell}>{(e.hours || 0).toFixed(2)}</td>
                  <td style={styles.tableCell}>{e.hotelspeesen ? 'Ja' : 'Nein'}</td>
                  <td style={styles.tableCell}>{e.verpflegungsMehraufwand ? 'Ja' : 'Nein'}</td>
                  <td style={styles.tableCell}>
                    {e.fachgebiet === 1 ? 'Workflow' : e.fachgebiet === 2 ? 'Finanzbuchhaltung' : 'Nein'}
                  </td>
                  <td style={styles.tableCell}>{e.homeoffice ? 'Ja' : 'Nein'}</td>
                  <td style={styles.tableCell}>{custName}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Beispiel-Charts */}
      <h3 style={styles.subtitle}>Stunden pro Kunde (aktuelle Filterung)</h3>
      {chartData.length > 0 ? (
        <BarChart width={600} height={300} data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="hours" fill="#8884d8" name="Stunden" />
        </BarChart>
      ) : (
        <p>Keine Daten für Diagramm verfügbar</p>
      )}
    </div>
  );
}

// Stile
const styles = {
  container: {
    padding: '1.5rem',
  },
  title: {
    textAlign: 'center',
    marginBottom: '1rem',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1rem',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  select: {
    padding: '0.4rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    minWidth: '120px',
  },
  exportBtn: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  summaryBox: {
    backgroundColor: '#f3f4f6',
    padding: '0.5rem',
    marginBottom: '1rem',
    borderRadius: '6px',
  },
  // Äußerer Container legt ggf. eine Breite fest
  tableOuterContainer: {
    width: '100%',
    marginBottom: '2rem',
  },
  // Innerer Container mit fester Höhe und Scrollbars
  tableInnerContainer: {
    // Höhe so wählen, dass ~50 Zeilen sichtbar sind (z. B. 600px oder 800px etc.)
    maxHeight: '600px',
    overflowY: 'auto',
    overflowX: 'auto',
    border: '1px solid #ddd',
    borderRadius: '6px',
  },
  // Tabelle selbst etwas breiter machen, sodass bei Bedarf horizontal gescrollt werden kann
  tableContainer: {
    overflow: 'auto',
    maxHeight: '60vh',  // Adjust viewport height as needed
    marginBottom: '2rem',
    border: '1px solid #e5e7eb',
  },
  table: {
    width: 'max-content',
    minWidth: '100%',
    borderCollapse: 'collapse',
    position: 'relative',
  },
  tableHeader: {
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 1,
    textAlign: 'center',
    padding: '12px',
    borderBottom: '1px solid #ddd',
    whiteSpace: 'nowrap',
    backgroundColor: '#f3f4f6',
    fontWeight: 'bold',
  },
  tableCell: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    marginTop: '2rem',
    marginBottom: '1rem',
  },
};
